import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import moment from 'moment';
import { StaffFeesOverdueReportService, OverdueFeeTermDetail } from '../services/staff-fees-overdue-report.service';
import { StaffSessionService } from '../services/staff-session.service';

export interface StaffFeesReportOverdueStudentHistoryNavState {
  studentName: string;
  className: string;
  classNumber?: string;
  classDivision?: string;
}

// Parallel to StaffFeesReportStudentHistoryComponent, but simpler: this
// screen (per spec) has no paidAmount, no isPaid split, no Paid/Unpaid tab
// toggle, and no feeStatus/colorCode tag on each fee card — it only ever
// shows the one overdue/unpaid breakdown.
@Component({
  selector: 'app-staff-fees-report-overdue-student-history',
  templateUrl: './staff-fees-report-overdue-student-history.component.html',
  styleUrls: ['../shared/staff-common.css', '../fees-report-student-history/staff-fees-report-student-history.component.css']
})
export class StaffFeesReportOverdueStudentHistoryComponent implements OnInit {
  staffId: number | string | null = null;
  currencySymbol = '';

  classId: string = '';
  studentId: string = '';
  className = '';
  classNumber = '';
  classDivision = '';
  studentName = '';

  fetchingStudentData = true;

  totalAmount = 0;
  pendingAmount = 0;
  feesPercentagePending = 0;

  feesInfo: OverdueFeeTermDetail[] = [];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private staffFeesOverdueReportService: StaffFeesOverdueReportService,
    private staffSession: StaffSessionService
  ) {}

  ngOnInit(): void {
    const staffDetails = this.staffSession.getStaffDetails();
    const info = this.staffSession.getSchoolAndStaffInfo();
    this.staffId = staffDetails?.staffId ?? info?.StaffId ?? null;
    this.currencySymbol = info?.currencySymbol || staffDetails?.currencyType || '';

    this.classId = this.route.snapshot.paramMap.get('classId') || '';
    this.studentId = this.route.snapshot.paramMap.get('studentId') || '';
    const state = history.state as StaffFeesReportOverdueStudentHistoryNavState;
    this.studentName = state?.studentName || '';
    this.className = state?.className || '';
    this.classNumber = state?.classNumber || '';
    this.classDivision = state?.classDivision || '';

    this.loadStudentHistory();
  }

  private async loadStudentHistory() {
    if (!this.staffId || !this.classId || !this.studentId) { this.fetchingStudentData = false; return; }
    this.fetchingStudentData = true;
    try {
      const response = await firstValueFrom(
        this.staffFeesOverdueReportService.getPendingStudentHistory(this.staffId, this.classId, this.studentId)
      );
      if (response?.status && response.body) {
        this.totalAmount = response.body.totalAmount || 0;
        this.pendingAmount = response.body.pendingAmount || 0;
        this.feesPercentagePending = response.body.percentagePendingAmount || 0;
        this.feesInfo = response.body.feesDetail || [];
      } else {
        this.resetToEmpty();
      }
    } catch {
      // No error toast — matches the mobile app, which falls through
      // silently to zeros/empty state on failure.
      this.resetToEmpty();
    } finally {
      this.fetchingStudentData = false;
    }
  }

  private resetToEmpty() {
    this.totalAmount = 0;
    this.pendingAmount = 0;
    this.feesPercentagePending = 0;
    this.feesInfo = [];
  }

  formatAmount(value: number): string {
    return `${this.currencySymbol}${(value || 0).toLocaleString('en-IN')}`;
  }

  formatDate(value: string | undefined): string {
    if (!value) { return ''; }
    const d = moment(value);
    return d.isValid() ? d.format('DD/MM/YYYY') : value;
  }

  goBack() {
    this.router.navigate(['/staff/fees-overdue-report/class', this.classId], {
      state: { classNumber: this.classNumber, classDivision: this.classDivision }
    });
  }
}
